import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tooltip } from 'antd';
import * as Store from '../store';
import * as Styled from './styled';
import type * as Types from '../../../types';

type BaseFilterValue = {
  fid: number;
  name: string;
  rule_text: string;
};

type BaseFilterWithOption<T extends BaseFilterValue> = { label: string; options: T[] };

type FilterOption<T extends BaseFilterValue> = {
  value: number;
  label: React.ReactNode;
  filterValue: T;
  ruleText: string;
  type: 'saved' | 'preset' | 'recent';
};

function isOptionGrouped<T extends BaseFilterValue, P extends BaseFilterWithOption<T>>(
  target: P | T,
): target is P {
  return (
    Reflect.has(target, 'options') &&
    Array.isArray(Reflect.get(target, 'options')) &&
    Reflect.has(target, 'label')
  );
}

export function generateFilterOptions<
  T extends BaseFilterValue,
  P extends { label: string; options: T[] },
>(options: (T | P)[] = []) {
  return options.map((it) => {
    if (isOptionGrouped(it)) {
      return {
        label: it.label,
        options: it.options.map(
          (item) =>
            ({
              value: item.fid,
              label: (
                <Tooltip title={item.rule_text} placement="right">
                  <Styled.TextEllipsisBlock>{item.name}</Styled.TextEllipsisBlock>
                </Tooltip>
              ),
              filterValue: item,
              ruleText: item.rule_text,
              type: 'recent' as const,
            }) as FilterOption<T>,
        ),
      };
    }

    return {
      value: it.fid,
      label: (
        <Tooltip title={it.rule_text} placement="right">
          <Styled.TextEllipsisBlock>{it.name}</Styled.TextEllipsisBlock>
        </Tooltip>
      ),
      filterValue: it,
      ruleText: it.rule_text,
      type: it.fid > 0 ? ('saved' as const) : ('preset' as const),
    } as FilterOption<T>;
  });
}

export const useRecentFilterOptions = (enabled = true) => {
  const presetFilters = Store.usePresetFilters();
  const onRecentFilterSearch = Store.useOnRecentFilterSearch();
  const recentFilters = useQuery(
    ['filter-dialog', 'filters', 'recent'],
    () => {
      if (onRecentFilterSearch) return onRecentFilterSearch();
      return [];
    },
    {
      enabled,
      cacheTime: 1000 * 3,
      staleTime: 1000 * 3,
      retry: 3,
    },
  );
  const options = React.useMemo(
    () =>
      generateFilterOptions([
        ...(presetFilters || []),
        { label: 'Recent Saved Filters', options: recentFilters.data || [] },
      ]),
    [presetFilters, recentFilters.data],
  );

  return {
    data: options,
    isLoading: recentFilters.isLoading,
  };
};

export const checkEmptyRule = (value: Types.FilterRuleValuePair[]) =>
  value.length === 1 && value[0].value === '' && value[0].display === '';
